import { useContext, useMemo } from "react";
import { TimeZoneContext } from "../contexts/TimeZoneContext";
import {
  DateFormatContext,
  DatetimeFullFormatContext,
  DatetimeISO8601FormatContext,
  DatetimeShortFormatContext,
} from "../contexts/DatetimeFormatContext";
import { hasValue, isNullish } from "../lib/util/common-util";
import { formatInTimeZone } from "date-fns-tz";

export function useFormattedDate(
  value?: Date,
  option: {
    date?: boolean;
    shortDatetime?: boolean;
    fullDatetime?: boolean;
    iso8601?: boolean;
  } = {
    date: true,
    shortDatetime: true,
    fullDatetime: true,
    iso8601: true,
  },
) {
  const timeZone = useContext(TimeZoneContext);
  const dateFormat = useContext(DateFormatContext);
  const datetimeShortFormat = useContext(DatetimeShortFormatContext);
  const datetimeFullFormat = useContext(DatetimeFullFormatContext);
  const iso8601Format = useContext(DatetimeISO8601FormatContext);

  const { dateText, shortDatetimeText, fullDatetimeText, iso8601Text } =
    useMemo(() => {
      if (isNullish(value))
        return {
          dateText: null,
          shortDatetimeText: null,
          fullDatetimeText: null,
          iso8601Text: null,
        };
      return {
        dateText:
          hasValue(option.date) && option.date
            ? formatInTimeZone(value, timeZone, dateFormat)
            : null,
        shortDatetimeText:
          hasValue(option.shortDatetime) && option.shortDatetime
            ? formatInTimeZone(value, timeZone, datetimeShortFormat)
            : null,
        fullDatetimeText:
          hasValue(option.fullDatetime) && option.fullDatetime
            ? formatInTimeZone(value, timeZone, datetimeFullFormat)
            : null,
        iso8601Text:
          hasValue(option.fullDatetime) && option.iso8601
            ? formatInTimeZone(value, timeZone, iso8601Format)
            : null,
      };
    }, [
      value,
      option.date,
      option.shortDatetime,
      option.fullDatetime,
      option.iso8601,
      timeZone,
      dateFormat,
      datetimeShortFormat,
      datetimeFullFormat,
      iso8601Format,
    ]);

  return { dateText, shortDatetimeText, fullDatetimeText, iso8601Text };
}
